import {
  ChangeEventHandler,
  InputHTMLAttributes,
  MouseEventHandler,
  PointerEventHandler,
  forwardRef,
} from "react";
import { cn } from "../../lib/utils";
import { normalizeTextInput } from "../../lib/text-input";

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  autoUppercase?: (boolean) | undefined;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      autoCapitalize,
      autoUppercase = false,
      className,
      onChange,
      onClick,
      onPointerDown,
      spellCheck,
      type,
      ...props
    },
    ref,
  ) => {
    const shouldAutoUppercase = autoUppercase && isTextInputType(type);

    const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      if (shouldAutoUppercase) {
        const normalizedValue = normalizeTextInput(event.currentTarget.value);

        event.currentTarget.value = normalizedValue;
        (event.target as HTMLInputElement).value = normalizedValue;
      }

      onChange?.(event);
    };

    const openNativePicker = (input: HTMLInputElement) => {
      if (type !== "time" && type !== "date") {
        return;
      }

      if (input.disabled || input.readOnly || typeof input.showPicker !== "function") {
        return;
      }

      try {
        input.showPicker();
      } catch {
        // Browsers without an available native picker still retain normal input behavior.
      }
    };

    const handlePointerDown: PointerEventHandler<HTMLInputElement> = (event) => {
      onPointerDown?.(event);

      if (event.defaultPrevented || event.button !== 0) {
        return;
      }

      // Chromium may route clicks on the native calendar/clock indicator through
      // the input's pointer event before the normal click event. Opening here
      // makes the icon itself a reliable picker trigger inside the Web workspace.
      openNativePicker(event.currentTarget);
    };

    const handleClick: MouseEventHandler<HTMLInputElement> = (event) => {
      onClick?.(event);

      if (event.defaultPrevented) {
        return;
      }

      // Keep the whole date/time field usable as a picker trigger as well as the
      // browser-provided calendar/clock indicator.
      openNativePicker(event.currentTarget);
    };

    return (
      <input
        ref={ref}
        type={type}
        autoCapitalize={shouldAutoUppercase ? (autoCapitalize ?? "characters") : autoCapitalize}
        spellCheck={shouldAutoUppercase ? (spellCheck ?? false) : spellCheck}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        onChange={handleChange}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        {...props}
      />
    );
  },
);

Input.displayName = "Input";

function isTextInputType(type: InputHTMLAttributes<HTMLInputElement>["type"]) {
  return type === undefined || type === "text" || type === "search";
}
